import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, ChevronRight, Clock } from "lucide-react";
import { parseCalendarDate } from "@/lib/dates";

function EventRow({ event }) {
  const d = parseCalendarDate(event.date);
  const hasImage = !!event.image_url;

  return (
    <Link
      to={`/events/${event.id}`}
      className="block group"
      data-testid={`event-row-${event.id}`}
    >
      <div className="bg-walnut border border-lv-border overflow-hidden transition-all duration-300 hover:border-bronze/40">
        {hasImage && (
          <div className="relative h-40 sm:h-48 overflow-hidden">
            <img
              src={event.image_url}
              alt={event.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-walnut via-walnut/30 to-transparent" />
            {event.event_type && (
              <span className="absolute top-4 left-4 text-[10px] tracking-[0.25em] uppercase bg-walnut-deep/80 text-sand px-3 py-1 backdrop-blur-sm">
                {event.event_type}
              </span>
            )}
          </div>
        )}
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {!hasImage && event.event_type && (
                <p className="text-[10px] tracking-[0.3em] uppercase text-lv-muted mb-1.5">
                  {event.event_type}
                </p>
              )}
              <h3 className="font-serif text-xl text-cream tracking-wide group-hover:text-sand transition-colors">
                {event.title}
              </h3>
              {event.description && (
                <p className="text-sm text-sand/60 mt-2 leading-relaxed line-clamp-2">
                  {event.description}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
                {d && (
                  <div className="flex items-center gap-1.5">
                    <CalendarDays size={12} className="text-bronze shrink-0" />
                    <span className="text-[11px] text-sand/80 tracking-wide">
                      {d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    </span>
                  </div>
                )}
                {event.location && (
                  <div className="flex items-center gap-1.5">
                    <MapPin size={12} className="text-lv-muted/60 shrink-0" />
                    <span className="text-[11px] text-lv-muted/70 tracking-wide">{event.location}</span>
                  </div>
                )}
              </div>
            </div>
            <ChevronRight size={18} className="text-lv-muted/40 group-hover:text-bronze transition-colors shrink-0 mt-2" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Events() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["member-events"],
    queryFn: () => api.get("/events"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="events-loading">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-32 px-6" data-testid="events-error">
        <p className="text-cream text-sm">{error.message}</p>
      </div>
    );
  }

  const events = data || [];

  return (
    <div className="max-w-3xl mx-auto" data-testid="events-page">
      <div className="mb-10 sm:mb-14">
        <h1 className="font-serif text-3xl sm:text-4xl text-cream tracking-wide">
          Events & Experiences
        </h1>
        <p className="text-sm text-lv-muted tracking-wide mt-2">
          Upcoming experiences for your chapter
        </p>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-16" data-testid="events-empty">
          <CalendarDays size={28} className="text-lv-muted/30 mx-auto mb-4" />
          <p className="text-sm text-lv-muted tracking-wide">
            No upcoming events at this time
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="events-list">
          {events.map((e) => (
            <EventRow key={e.id} event={e} />
          ))}
        </div>
      )}
    </div>
  );
}
